import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  QuestionLayout,
  RectangularOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { VAGINAL_INFECTION_OPTIONS } from "../options";
import type { VaginalInfectionFrequency } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function VaginalInfectionsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  // Map state to the selected option
  const getSelection = (): VaginalInfectionFrequency | null => {
    if (state.hasVaginalInfectionHistory === null) return null;
    if (state.hasVaginalInfectionHistory === false) return "none";
    // Check the first infection history entry for the frequency
    const first = state.vaginalInfectionHistories[0];
    if (first?.vaginalInfectionHistory === "often") return "often";
    if (first?.vaginalInfectionHistory === "past") return "past";
    return "often"; // Default for true
  };

  const selection = getSelection();

  const handleChange = (value: VaginalInfectionFrequency) => {
    if (value === "none") {
      const idsToDelete = state.vaginalInfectionHistories
        .filter((v) => v.id)
        .map((v) => v.id!);
      updateState({
        hasVaginalInfectionHistory: false,
        vaginalInfectionHistories: [],
        infectionIdsToDelete: [...state.infectionIdsToDelete, ...idsToDelete],
      });
    } else {
      // Create or update the infection entry with the frequency
      const existing = state.vaginalInfectionHistories[0];
      const entry = {
        ...existing,
        vaginalInfectionHistory: value,
      };
      updateState({
        hasVaginalInfectionHistory: true,
        vaginalInfectionHistories: [entry],
      });
    }
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("VaginalInfections");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.vaginal_infections_title")}
      subtitle={t(
        "med_history.gynae.sexual_health.vaginal_infections_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        {VAGINAL_INFECTION_OPTIONS.map((option) => (
          <RectangularOptionButton
            key={option.id}
            icon={option.icon}
            label={t(option.labelKey)}
            isSelected={selection === option.id}
            onPress={() => handleChange(option.id)}
          />
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 12,
  },
});
